'use client'

import { CartsContext } from '@context/cart.ctx'
import { zodResolver } from '@hookform/resolvers/zod'
import { useContext, useState, useEffect } from 'react'
import {
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { nineNineIntegratorApi } from 'src/lib/axios'
import { z } from 'zod'
import Cart from '../../../types/cart'

interface NineNineOrderModalProps {
  onAccept?: () => void
  onReject?: () => void
}

const cancelOrderSchema = z.object({
  reason_id: z.number().min(1, 'Selecione um motivo de cancelamento'),
  reason: z.string().min(10, 'O motivo deve ter pelo menos 10 caracteres'),
})

type CancelOrderFormData = z.infer<typeof cancelOrderSchema>

const CANCEL_REASONS = [
  { id: 1010, label: 'Item esgotado' },
  { id: 1020, label: 'Loja fechada' },
  { id: 1030, label: 'A loja está muito ocupada para preparar a refeição' },
  {
    id: 1040,
    label:
      'Não é possível preparar a refeição devido a falta de água ou energia',
  },
  { id: 1050, label: 'Cancelamento solicitado pelo cliente' },
  { id: 1060, label: 'Sem entregador para entrega' },
  { id: 1080, label: 'Outro motivo' },
] as const

export const NineNineOrderModal = ({
  onAccept,
  onReject,
}: NineNineOrderModalProps) => {
  const { nineNineOrders, setNineNineOrders } = useContext(CartsContext)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CancelOrderFormData>({
    resolver: zodResolver(cancelOrderSchema),
    defaultValues: {
      reason_id: 0,
      reason: '',
    },
  })

  // Apenas orders do NineNine
  const cart = nineNineOrders.filter((o: any) => {
    return o.status === null
  })[0]

  if (!cart) return null

  const handleCancelOrder = async (data: CancelOrderFormData, cart: Cart) => {
    setIsCanceling(true)
    try {
      await nineNineIntegratorApi.post(`/orders/update/${cart.orderId}`, {
        dispatchType: 'CANCELLED',
        reason_id: data.reason_id,
        reason: data.reason,
      })
      // update nineNineOrders state
      setNineNineOrders((prev: any[]) =>
        prev.map((order) => {
          if (order.id === cart.id) order.status = 'canceled'
          return order
        })
      )

      setShowCancelModal(false)
      reset()
      onReject?.()
    } catch (error) {
      console.error('Erro ao cancelar pedido 99Food', error)
      // feedback simples — o provider global costuma exibir toasts; aqui exibimos alerta rápido
      alert('Falha ao cancelar o pedido. Tente novamente.')
    } finally {
      setIsCanceling(false)
    }
  }

  const handleOpenCancelModal = () => setShowCancelModal(true)
  const handleCloseCancelModal = () => {
    setShowCancelModal(false)
    reset()
  }

  const handleConfirmOrder = async (cart: Cart) => {
    setIsConfirming(true)
    try {
      await nineNineIntegratorApi.post(`/orders/update/${cart.orderId}`, {
        dispatchType: 'confirm',
      })
      setNineNineOrders((prev: any[]) =>
        prev.map((order) => {
          if (order.id === cart.id) order.status = 'production'
          return order
        })
      )
      onAccept?.()
    } catch (error) {
      console.error('Erro ao confirmar pedido 99Food', error)
      alert('Falha ao confirmar o pedido. Tente novamente.')
    } finally {
      setShowCancelModal(false)
      setIsConfirming(false)
    }
  }

  let cartType = 'Delivery'
  switch (cart.type) {
    case 'D':
      cartType = 'Delivery'
      break
    case 'P':
      cartType = 'Encomenda'
      break
    case 'T':
      cartType = 'Mesa'
      break
    default:
      cartType = 'Delivery'
  }

  return (
    <Modal
      show
      onHide={() => {}}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <ModalHeader>
        <ModalTitle className="fw-bold">Pedido 99Food</ModalTitle>
      </ModalHeader>

      <ModalBody className="p-4">
        <div className="mb-4">
          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Cód. Pedido:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.code}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Nome:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.client?.name}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Total:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">R$ {cart?.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Pagamento:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.formsPayment[0].label}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Tipo:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cartType}</span>
            </div>
          </div>
        </div>

        <div className="border-top mb-3 pt-3">
          <div className="row mb-3">
            <div className="col-6">
              <h6 className="fw-semibold mb-0">Itens</h6>
            </div>
            <div className="col-6 text-end">
              <h6 className="fw-semibold mb-0">Preços</h6>
            </div>
          </div>

          {cart?.itens.map((item: any, index: number) => (
            <div key={item.controls?.mdu_id || `${item.id}-${index}`} className="mb-3">
              <div className="row">
                <div className="col-8">
                  <p className="fw-medium mb-1">{item.name}</p>
                  {item.obs && (
                    <p className="text-muted small my-0 mb-0 ms-3 p-0">
                      • {item.obs}
                    </p>
                  )}
                </div>
                <div className="col-4 text-end">
                  <span className="fw-semibold">
                    {item.details.value.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {cart?.obs && (
          <div className="border-top pt-3">
            <p className="small text-muted mb-0">
              <span className="fw-medium">Obs.:</span> {cart.obs}
            </p>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top">
        <div className="d-flex w-100 gap-2">
          <button
            onClick={handleOpenCancelModal}
            className="btn btn-outline-danger flex-fill fw-semibold"
            disabled={isConfirming || isCanceling}
          >
            Recusar Pedido
          </button>
          <button
            onClick={() => handleConfirmOrder(cart!)}
            className="btn btn-primary flex-fill fw-semibold"
            disabled={isConfirming || isCanceling}
          >
            {isConfirming ? 'Confirmando...' : 'Aceitar Pedido'}
          </button>
        </div>
      </ModalFooter>

      <Modal
        show={showCancelModal}
        onHide={handleCloseCancelModal}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader>
          <ModalTitle className="fw-bold">Confirmar Cancelamento</ModalTitle>
        </ModalHeader>

        <ModalBody className="p-4">
          <p className="mb-3">Preencha os dados para cancelar o pedido:</p>

          <Form
            id="cancel-order-form"
            onSubmit={handleSubmit((data) => handleCancelOrder(data, cart!))}
          >
            <div className="mb-3">
              <label htmlFor="reason_id" className="form-label fw-medium">
                Motivo do cancelamento:
              </label>
              <select
                {...register('reason_id', { valueAsNumber: true })}
                className={`form-select ${errors.reason_id ? 'is-invalid' : ''}`}
              >
                <option value={0}>Selecione um motivo</option>
                {CANCEL_REASONS.map((reason) => (
                  <option key={reason.id} value={reason.id}>
                    {reason.label}
                  </option>
                ))}
              </select>
              {errors.reason_id && (
                <div className="invalid-feedback">
                  {errors.reason_id.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="reason" className="form-label fw-medium">
                Motivo detalhado:
              </label>
              <textarea
                {...register('reason')}
                className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                rows={4}
                placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)..."
              />
              {errors.reason && (
                <div className="invalid-feedback">{errors.reason.message}</div>
              )}
              <div className="form-text">
                {watch('reason')?.length || 0}/10 caracteres mínimos
              </div>
            </div>

            <div className="alert alert-warning">
              <small>
                <strong>Atenção:</strong> Esta ação não pode ser desfeita. O
                pedido será cancelado permanentemente.
              </small>
            </div>
          </Form>
        </ModalBody>

        <ModalFooter className="border-top">
          <div className="d-flex w-100 gap-2">
            <button
              type="button"
              onClick={handleCloseCancelModal}
              className="btn btn-outline-secondary flex-fill fw-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="cancel-order-form"
              className="btn btn-danger flex-fill fw-semibold"
              disabled={isCanceling}
            >
              {isCanceling ? 'Cancelando...' : 'Confirmar Cancelamento'}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </Modal>
  )
}
